export default {
  id: 'running-a-backtest',
  title: 'Running a Backtest',
  content: `## Running a Backtest

Here is the step-by-step process to configure and run a backtest on a strategy.

---

## Step 1 — Open Backtesting

**Profile → Backtesting → New Backtest**

---

## Step 2 — Select or Define a Strategy

You can backtest:
- An **existing Algo Flow strategy** — select it from your saved strategies
- A **new set of conditions** — define them inline without saving as a full Algo Flow

If testing an existing strategy, the conditions are pre-filled from your saved strategy configuration.

---

## Step 3 — Select Symbols

Choose which NSE stocks to include in the backtest. You can select:
- Individual stocks (e.g., RELIANCE, INFY, TCS)
- A predefined group (e.g., all Nifty50 stocks, a sector)

A broader symbol set gives you more data points but takes longer to run.

---

## Step 4 — Set the Date Range

Select the historical period to test over.

| Available range | Notes |
|---|---|
| Last 7 days (intraday) | 1-minute candle data — for intraday condition testing |
| Last 30 days (daily) | Daily OHLCV — for swing condition testing |
| Last 90 days (daily) | Longer validation window — recommended minimum for swing strategies |

> A backtest on only 7 days of data is not statistically meaningful. Use at least 30 days and ideally 60–90 days for swing strategies.

---

## Step 5 — Set Exit Parameters

Define how simulated trades are exited:

| Parameter | Description |
|---|---|
| Stop loss % | Exit if price drops X% from entry |
| Target % | Exit if price rises X% from entry |
| Max hold days | Force exit after N trading days if neither stop nor target is hit |

These exit parameters mirror what you would set in a real trade and determine the P&L of each simulated instance.

---

## Step 6 — Run the Backtest

Tap **Run Backtest**. The engine processes each day and symbol in sequence, evaluating your conditions and recording each simulated trade.

Run time depends on:
- Number of symbols selected
- Date range length
- Whether you are using intraday or daily data

Typical runs complete within 10–30 seconds.

---

## Step 7 — Review Results

When complete, the results screen opens automatically. See the **Reading Results** topic for how to interpret the output.

---

## Tips for Meaningful Backtests

- Use at least **30 trading days** — short windows can be dominated by a single market regime
- Test on **multiple symbols** — if a strategy only works on one stock, it may be overfitted
- Test **with strict conditions first** (AND logic, specific thresholds) — then loosen if no results
- Compare the backtest period to a **market context** — was it predominantly trending or sideways?`,
};
